import React, { useState, useMemo } from 'react';
import { Eye, EyeOff, Copy, Edit2, Trash2, Share2, CreditCard, Code, Key, Star } from 'lucide-react';
import { maskCardNumber } from '../../services/creditCardService';
import { decryptApiKeyValue } from '../../services/apiKeyService';
import { formatDate } from '../../utils/dateUtils';
import { useAuth } from '../../contexts/AuthContext';
import './UnifiedItemList.css';

function UnifiedItemList({ items = [], onEdit, onDelete, onShare, onToggleFavorite, loading = false }) {
  const { user } = useAuth();
  const [visibleItems, setVisibleItems] = useState({});
  const [copyStates, setCopyStates] = useState({});
  const [decryptedKeys, setDecryptedKeys] = useState({});

  // 아이템별 표시 토글
  const toggleVisibility = (itemId) => {
    setVisibleItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // 복사 기능
  const handleCopy = async (text, itemId, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyStates(prev => ({
        ...prev,
        [`${itemId}-${field}`]: 'success'
      }));
      
      setTimeout(() => {
        setCopyStates(prev => ({
          ...prev,
          [`${itemId}-${field}`]: null
        }));
      }, 2000);
    } catch (error) {
      setCopyStates(prev => ({
        ...prev,
        [`${itemId}-${field}`]: 'error'
      }));
    }
  };

  // API 키 토글
  const handleApiKeyToggle = async (item) => {
    const isCurrentlyVisible = visibleItems[item.id];
    
    if (!isCurrentlyVisible) {
      // 보이게 할 때 복호화
      try {
        const decryptedValue = await decryptApiKeyValue(user.uid, item.keyValue);
        setDecryptedKeys(prev => ({
          ...prev,
          [item.id]: decryptedValue
        }));
        setVisibleItems(prev => ({
          ...prev,
          [item.id]: true
        }));
      } catch (error) {
        console.error('API 키 복호화 실패:', error);
        alert('API 키를 복호화할 수 없습니다.');
      }
    } else {
      // 숨길 때
      setVisibleItems(prev => ({
        ...prev,
        [item.id]: false
      }));
      // 복호화된 키도 메모리에서 제거
      setDecryptedKeys(prev => {
        const newKeys = { ...prev };
        delete newKeys[item.id];
        return newKeys;
      });
    }
  };

  // API 키 복사
  const handleApiKeyCopy = async (item) => {
    const decryptedValue = decryptedKeys[item.id];
    if (decryptedValue) {
      await handleCopy(decryptedValue, item.id, 'keyValue');
    }
  };

  // 아이템 타입별 아이콘
  const getItemIcon = (itemType) => {
    switch (itemType) {
      case 'password':
        return <Key size={20} />;
      case 'creditcard':
        return <CreditCard size={20} />;
      case 'apikey':
        return <Code size={20} />;
      default:
        return <Key size={20} />;
    }
  };

  // 아이템 타입별 라벨
  const getItemTypeLabel = (itemType) => {
    switch (itemType) {
      case 'password':
        return '패스워드';
      case 'creditcard':
        return '신용카드';
      case 'apikey':
        return 'API 키';
      default:
        return '항목';
    }
  };

  // 아이템별 주요 정보 렌더링
  const renderItemContent = (item) => {
    const isVisible = visibleItems[item.id];

    switch (item.itemType) {
      case 'password':
        return (
          <div className="item-content-password">
            <div className="item-field">
              <span className="field-label">사용자명:</span>
              <span className="field-value">{item.username || '-'}</span>
              <button
                className="btn-copy"
                onClick={() => handleCopy(item.username, item.id, 'username')}
                disabled={!item.username}
              >
                <Copy size={14} />
              </button>
            </div>
            <div className="item-field">
              <span className="field-label">비밀번호:</span>
              <span className="field-value">
                {isVisible ? item.password : '••••••••'}
              </span>
              <button
                className="btn-toggle"
                onClick={() => toggleVisibility(item.id)}
              >
                {isVisible ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              <button
                className="btn-copy"
                onClick={() => handleCopy(item.password, item.id, 'password')}
              >
                <Copy size={14} />
              </button>
            </div>
            {item.website && (
              <div className="item-field">
                <span className="field-label">웹사이트:</span>
                <a href={item.website} target="_blank" rel="noopener noreferrer">
                  {item.website}
                </a>
              </div>
            )}
          </div>
        );

      case 'creditcard':
        return (
          <div className="item-content-creditcard">
            <div className="item-field">
              <span className="field-label">카드번호:</span>
              <span className="field-value">
                {isVisible ? item.cardNumber : maskCardNumber(item.cardNumber)}
              </span>
              <button
                className="btn-toggle"
                onClick={() => toggleVisibility(item.id)}
              >
                {isVisible ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              <button
                className="btn-copy"
                onClick={() => handleCopy(item.cardNumber, item.id, 'cardNumber')}
              >
                <Copy size={14} />
              </button>
            </div>
            <div className="item-field">
              <span className="field-label">만료일:</span>
              <span className="field-value">
                {item.expiryMonth}/{item.expiryYear}
              </span>
            </div>
            <div className="item-field">
              <span className="field-label">소유자:</span>
              <span className="field-value">{item.cardHolder}</span>
            </div>
            {item.bank && (
              <div className="item-field">
                <span className="field-label">은행:</span>
                <span className="field-value">{item.bank}</span>
              </div>
            )}
          </div>
        );

      case 'apikey':
        return (
          <div className="item-content-apikey">
            <div className="item-field">
              <span className="field-label">키 값:</span>
              <span className="field-value">
                {isVisible ? (decryptedKeys[item.id] || '복호화 중...') : (item.keyValueMasked || '••••••••••••••••')}
              </span>
              <button
                className="btn-toggle"
                onClick={() => handleApiKeyToggle(item)}
                title={isVisible ? "숨기기" : "보기"}
              >
                {isVisible ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              <button
                className="btn-copy"
                onClick={() => handleApiKeyCopy(item)}
                disabled={!isVisible || !decryptedKeys[item.id]}
                title={isVisible ? "복사" : "먼저 키를 표시하세요"}
              >
                <Copy size={14} />
              </button>
            </div>
            <div className="item-field">
              <span className="field-label">제공자:</span>
              <span className="field-value">{item.provider}</span>
            </div>
            <div className="item-field">
              <span className="field-label">환경:</span>
              <span className={`environment-badge env-${item.environment}`}>
                {item.environment}
              </span>
            </div>
            {item.expiresAt && (
              <div className="item-field">
                <span className="field-label">만료일:</span>
                <span className={`field-value ${item.isExpired ? 'text-danger' : ''}`}>
                  {formatDate(item.expiresAt)}
                  {item.isExpired && ' (만료됨)'}
                </span>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // 그룹화된 아이템
  const groupedItems = useMemo(() => {
    const groups = {
      favorites: [],
      passwords: [],
      creditcards: [],
      apikeys: []
    };

    items.forEach(item => {
      if (item.isFavorite) {
        groups.favorites.push(item);
      } else {
        switch (item.itemType) {
          case 'password':
            groups.passwords.push(item);
            break;
          case 'creditcard':
            groups.creditcards.push(item);
            break;
          case 'apikey':
            groups.apikeys.push(item);
            break;
        }
      }
    });

    return groups;
  }, [items]);

  if (loading) {
    return (
      <div className="items-loading">
        <div className="spinner" />
        <p>아이템을 불러오는 중...</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="items-empty">
        <p>등록된 아이템이 없습니다.</p>
      </div>
    );
  }

  return (
    <div className="unified-item-list">
      {/* 즐겨찾기 섹션 */}
      {groupedItems.favorites.length > 0 && (
        <div className="item-section">
          <h3 className="section-title">⭐ 즐겨찾기</h3>
          <div className="item-grid">
            {groupedItems.favorites.map(item => (
              <div key={item.id} className={`item-card item-type-${item.itemType}`}>
                <div className="item-header">
                  <div className="item-title">
                    {getItemIcon(item.itemType)}
                    <span>{item.title || item.cardName || item.keyName}</span>
                  </div>
                  <div className="item-header-actions">
                    <button
                      className={`btn-favorite ${item.isFavorite ? 'active' : ''}`}
                      onClick={() => onToggleFavorite && onToggleFavorite(item.itemType, item.id, !item.isFavorite)}
                      title={item.isFavorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}
                    >
                      <Star size={16} fill={item.isFavorite ? 'currentColor' : 'none'} />
                    </button>
                    <span className="item-type-badge">{getItemTypeLabel(item.itemType)}</span>
                  </div>
                </div>
                
                {renderItemContent(item)}
                
                {item.tags && item.tags.length > 0 && (
                  <div className="item-tags">
                    {item.tags.map(tag => (
                      <span key={tag} className="tag">{tag}</span>
                    ))}
                  </div>
                )}
                
                <div className="item-actions">
                  <button
                    className="btn-action"
                    onClick={() => onEdit(item.itemType, item)}
                    title="수정"
                  >
                    <Edit2 size={16} />
                  </button>
                  {item.itemType === 'password' && onShare && (
                    <button
                      className="btn-action"
                      onClick={() => onShare(item)}
                      title="공유"
                    >
                      <Share2 size={16} />
                    </button>
                  )}
                  <button
                    className="btn-action btn-danger"
                    onClick={() => onDelete(item.itemType, item.id)}
                    title="삭제"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 타입별 섹션 */}
      {Object.entries(groupedItems).map(([type, items]) => {
        if (type === 'favorites' || items.length === 0) return null;
        
        const typeLabel = {
          passwords: '패스워드',
          creditcards: '신용카드',
          apikeys: 'API 키'
        }[type];

        return (
          <div key={type} className="item-section">
            <h3 className="section-title">{typeLabel} ({items.length})</h3>
            <div className="item-grid">
              {items.map(item => (
                <div key={item.id} className={`item-card item-type-${item.itemType}`}>
                  <div className="item-header">
                    <div className="item-title">
                      {getItemIcon(item.itemType)}
                      <span>{item.title || item.cardName || item.keyName}</span>
                    </div>
                    <div className="item-header-actions">
                      <button
                        className={`btn-favorite ${item.isFavorite ? 'active' : ''}`}
                        onClick={() => onToggleFavorite && onToggleFavorite(item.itemType, item.id, !item.isFavorite)}
                        title={item.isFavorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}
                      >
                        <Star size={16} fill={item.isFavorite ? 'currentColor' : 'none'} />
                      </button>
                      <span className="item-type-badge">{getItemTypeLabel(item.itemType)}</span>
                    </div>
                  </div>
                  
                  {renderItemContent(item)}
                  
                  {item.tags && item.tags.length > 0 && (
                    <div className="item-tags">
                      {item.tags.map(tag => (
                        <span key={tag} className="tag">{tag}</span>
                      ))}
                    </div>
                  )}
                  
                  <div className="item-actions">
                    <button
                      className="btn-action"
                      onClick={() => onEdit(item.itemType, item)}
                      title="수정"
                    >
                      <Edit2 size={16} />
                    </button>
                    {item.itemType === 'password' && onShare && (
                      <button
                        className="btn-action"
                        onClick={() => onShare(item)}
                        title="공유"
                      >
                        <Share2 size={16} />
                      </button>
                    )}
                    <button
                      className="btn-action btn-danger"
                      onClick={() => onDelete(item.itemType, item.id)}
                      title="삭제"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default UnifiedItemList;